import {
  CustomerEvalSuiteId,
  CustomerEvalSuiteVersionId,
  MagicRulesFileId,
  OrgGuideFileId,
  OrgGuideFileVersionId,
  OrgId,
  SemanticProjectFileId,
  SemanticProjectId,
  SemanticProjectVersionId,
  UserId,
  guardNever,
} from "@hex/common";

import {
  ContextWorkbenchEntity,
  ContextWorkbenchEntityRef,
} from "../contextWorkbenchEntity";
import {
  ContextWorkbenchFile,
  ContextWorkbenchFileId,
} from "../contextWorkbenchFile";

export function getFileId(params: {
  type: ContextWorkbenchFile.Type.Entity;
  entityRef: ContextWorkbenchEntityRef;
}): ContextWorkbenchFileId {
  if (params.type === ContextWorkbenchFile.Type.Entity) {
    return getFileIdForEntityRef(params.entityRef);
  } else {
    guardNever(params.type, params.type);
    return ContextWorkbenchFileId.parse("unknown");
  }
}

/**
 * Creates a deterministic file ID from an entity type and entity ID.
 * This ensures stable file IDs across sessions for the same entity.
 */
export function getFileIdForEntityRef(
  entityRef: ContextWorkbenchEntityRef,
): ContextWorkbenchFileId {
  if (entityRef.type === ContextWorkbenchEntity.Type.MagicRulesFile) {
    return getFileIdForMagicRulesFileEntity(entityRef);
  } else if (entityRef.type === ContextWorkbenchEntity.Type.OrgGuideFile) {
    return getFileIdForOrgGuideFileEntity(entityRef);
  } else if (
    entityRef.type === ContextWorkbenchEntity.Type.WorkspaceContextFile
  ) {
    return getFileIdForWorkspaceContextFileEntity(entityRef);
  } else if (
    entityRef.type === ContextWorkbenchEntity.Type.SemanticProjectFile
  ) {
    return getFileIdForSemanticProjectFileEntity(entityRef);
  } else if (entityRef.type === ContextWorkbenchEntity.Type.EvalSuiteFile) {
    return getFileIdForEvalSuiteFileEntity(entityRef);
  } else {
    guardNever(entityRef, entityRef);
    return ContextWorkbenchFileId.parse("unknown");
  }
}

const SEMANTIC_RESOURCE_SELECTION_ID_PREFIX = "Resource";

/**
 * Builds a stable selection id for a semantic resource row so it can share
 * file list selection state
 */
export function getSemanticResourceSelectionId(
  semanticProjectId: SemanticProjectId,
  resourceKey: string,
): ContextWorkbenchFileId {
  return ContextWorkbenchFileId.parse(
    `${SEMANTIC_RESOURCE_SELECTION_ID_PREFIX}:${semanticProjectId}:${resourceKey}`,
  );
}

const getFileIdForMagicRulesFileEntity = (
  entityRef: ContextWorkbenchEntityRef.MagicRulesFile,
): ContextWorkbenchFileId => {
  return ContextWorkbenchFileId.parse(
    `${ContextWorkbenchFile.Type.Entity}:${entityRef.type}:${entityRef.orgId}:${entityRef.userId}:${entityRef.magicRulesFileId}`,
  );
};

const getFileIdForOrgGuideFileEntity = (
  entityRef: ContextWorkbenchEntityRef.OrgGuideFile,
): ContextWorkbenchFileId => {
  return ContextWorkbenchFileId.parse(
    `${ContextWorkbenchFile.Type.Entity}:${entityRef.type}:${entityRef.orgId}:${entityRef.orgGuideFileId}:${entityRef.orgGuideFileVersionId}`,
  );
};

const getFileIdForWorkspaceContextFileEntity = (
  entityRef: ContextWorkbenchEntityRef.WorkspaceContextFile,
): ContextWorkbenchFileId => {
  return ContextWorkbenchFileId.parse(
    `${ContextWorkbenchFile.Type.Entity}:${entityRef.type}:${entityRef.orgId}:${entityRef.orgGuideFileId}:${entityRef.orgGuideFileVersionId}`,
  );
};

const getFileIdForSemanticProjectFileEntity = (
  entityRef: ContextWorkbenchEntityRef.SemanticProjectFile,
): ContextWorkbenchFileId => {
  return ContextWorkbenchFileId.parse(
    `${ContextWorkbenchFile.Type.Entity}:${entityRef.type}:${entityRef.orgId}:${entityRef.semanticProjectId}:${entityRef.semanticProjectVersionId}:${entityRef.semanticProjectFileId}`,
  );
};

const getFileIdForEvalSuiteFileEntity = (
  entityRef: ContextWorkbenchEntityRef.EvalSuiteFile,
): ContextWorkbenchFileId => {
  return ContextWorkbenchFileId.parse(
    `${ContextWorkbenchFile.Type.Entity}:${entityRef.type}:${entityRef.orgId}:${entityRef.customerEvalSuiteId}:${entityRef.customerEvalSuiteVersionId}`,
  );
};

/**
 * Parses a composite file ID back into its entity type and entity ID components.
 * Returns null if the file ID is not in the expected format.
 */
export function parseFileId(
  fileId: ContextWorkbenchFileId,
): ContextWorkbenchEntityRef | null {
  // Resource selection ids share the ContextWorkbenchFileId brand for file list
  // selection state, but point at derived diff rows rather than file entities
  if (fileId.startsWith(SEMANTIC_RESOURCE_SELECTION_ID_PREFIX)) {
    return null;
  }

  const parts = fileId.split(":");
  if (parts.length === 0) return null;
  // Being liberal with casting here because it's parsing, kind of a necessary evil
  const fileType = parts[0] as ContextWorkbenchFile.Type;
  if (fileType === ContextWorkbenchFile.Type.Entity) {
    return parseEntityFileId(parts);
  } else {
    guardNever(fileType, fileType);
    return null;
  }
}

const parseEntityFileId = (
  parts: string[],
): ContextWorkbenchEntityRef | null => {
  const entityType = parts[1] as ContextWorkbenchEntity.Type;
  if (entityType === ContextWorkbenchEntity.Type.MagicRulesFile) {
    return parseMagicRulesFileEntityFileId(parts);
  } else if (entityType === ContextWorkbenchEntity.Type.OrgGuideFile) {
    return parseOrgGuideFileDraftEntityFileId(parts);
  } else if (entityType === ContextWorkbenchEntity.Type.WorkspaceContextFile) {
    return parseWorkspaceContextFileDraftEntityFileId(parts);
  } else if (entityType === ContextWorkbenchEntity.Type.SemanticProjectFile) {
    return parseSemanticProjectFileEntityFileId(parts);
  } else if (entityType === ContextWorkbenchEntity.Type.EvalSuiteFile) {
    return parseEvalSuiteFileEntityFileId(parts);
  } else {
    guardNever(entityType, entityType);
    return null;
  }
};

const parseMagicRulesFileEntityFileId = (
  parts: string[],
): ContextWorkbenchEntityRef.MagicRulesFile | null => {
  if (parts.length !== 5) return null;
  const orgId = OrgId.parse(parts[2]);
  const userId = parts[3] === "null" ? null : UserId.safeParse(parts[3]).data;
  if (userId === undefined) return null;
  const magicRulesFileId = MagicRulesFileId.safeParse(parts[4]).data;
  if (magicRulesFileId == null) return null;
  return {
    type: ContextWorkbenchEntity.Type.MagicRulesFile,
    orgId,
    userId,
    magicRulesFileId,
  };
};

const parseOrgGuideFileDraftEntityFileId = (
  parts: string[],
): ContextWorkbenchEntityRef.OrgGuideFile | null => {
  if (parts.length !== 5) return null;
  const orgId = OrgId.parse(parts[2]);
  const orgGuideFileId = OrgGuideFileId.safeParse(parts[3]).data;
  if (orgGuideFileId == null) return null;
  const orgGuideFileVersionId = OrgGuideFileVersionId.safeParse(parts[4]).data;
  if (orgGuideFileVersionId == null) return null;
  return {
    type: ContextWorkbenchEntity.Type.OrgGuideFile,
    orgId,
    orgGuideFileId,
    orgGuideFileVersionId,
  };
};

const parseWorkspaceContextFileDraftEntityFileId = (
  parts: string[],
): ContextWorkbenchEntityRef.WorkspaceContextFile | null => {
  if (parts.length !== 5) return null;
  const orgId = OrgId.parse(parts[2]);
  const orgGuideFileId = OrgGuideFileId.safeParse(parts[3]).data;
  if (orgGuideFileId == null) return null;
  const orgGuideFileVersionId = OrgGuideFileVersionId.safeParse(parts[4]).data;
  if (orgGuideFileVersionId == null) return null;
  return {
    type: ContextWorkbenchEntity.Type.WorkspaceContextFile,
    orgId,
    orgGuideFileId,
    orgGuideFileVersionId,
  };
};

const parseEvalSuiteFileEntityFileId = (
  parts: string[],
): ContextWorkbenchEntityRef.EvalSuiteFile | null => {
  if (parts.length !== 5) return null;
  const orgId = OrgId.parse(parts[2]);
  const customerEvalSuiteId = CustomerEvalSuiteId.safeParse(parts[3]).data;
  if (customerEvalSuiteId == null) return null;
  const customerEvalSuiteVersionId = CustomerEvalSuiteVersionId.safeParse(
    parts[4],
  ).data;
  if (customerEvalSuiteVersionId == null) return null;
  return {
    type: ContextWorkbenchEntity.Type.EvalSuiteFile,
    orgId,
    customerEvalSuiteId,
    customerEvalSuiteVersionId,
  };
};

const parseSemanticProjectFileEntityFileId = (
  parts: string[],
): ContextWorkbenchEntityRef.SemanticProjectFile | null => {
  if (parts.length !== 6) return null;
  const orgId = OrgId.parse(parts[2]);
  const semanticProjectId = SemanticProjectId.safeParse(parts[3]).data;
  if (semanticProjectId == null) return null;
  const semanticProjectVersionId = SemanticProjectVersionId.safeParse(
    parts[4],
  ).data;
  if (semanticProjectVersionId == null) return null;
  const semanticProjectFileId = SemanticProjectFileId.safeParse(parts[5]).data;
  if (semanticProjectFileId == null) return null;
  return {
    type: ContextWorkbenchEntity.Type.SemanticProjectFile,
    orgId,
    semanticProjectId,
    semanticProjectVersionId,
    semanticProjectFileId,
  };
};
